import { HttpClient } from '@angular/common/http';
import { EventEmitter, Injectable, Type } from '@angular/core';
import { ApplicationInsights } from '@microsoft/applicationinsights-web';
import { environment } from 'environments/environment';
import { firstValueFrom } from 'rxjs';

import { EntityChangeLogMetaDataType } from 'enums/EntityChangeLogMetaDataType';
import { UserModel } from 'models/generated/Identity.Api.Models.User';
import { LogRequestModel } from 'models/generated/Logging.Api.Models.LogRequest';
import { UserService } from 'services/user.service';
import { RelationService } from './relation.service';

export class LogEvent {
	timestamp?: Date;
	entityId: string;
	entityType: string;
	primaryRelationEntityId: string;
	primaryRelationEntityType: string;
	entityState: string;
	userId: string;
	metaData: object;
	metaDataType: EntityChangeLogMetaDataType;
	user?: UserModel;
	previousValue?: Record<string, unknown>;
	currentValue?: Record<string, unknown>;
	activityId?: string;

	constructor() {
		this.entityId = '';
		this.entityType = '';
		this.primaryRelationEntityId = '';
		this.primaryRelationEntityType = '';
		this.entityState = '';
		this.userId = '';
		this.metaData = {};
		this.metaDataType = 0;		
	}
}

export class LogEventOfEntity<T> extends LogEvent {
	previousValueOfEntity?: T;
	currentValueOfEntity?: T;
}

@Injectable()
export class LoggingService  {
	private baseUrl = `${environment.gatewayUrl}/api/changelog`;
	private appInsights!: ApplicationInsights;
	private exceptionHistory: string[] = [];

	public onError = new EventEmitter<Error>();
		
	constructor(private readonly http: HttpClient, private readonly userService: UserService) { }

	resolve(): Promise<void> | void {
		return new Promise<void>((resolve) => {
			this.startApplicationInsightsMonitoring();
			resolve();			
		});
	}

	startApplicationInsightsMonitoring(): void {
		this.appInsights = new ApplicationInsights({
			config: {
				instrumentationKey: environment.appInsightsInstrumentationKey,
				autoTrackPageVisitTime: true,
				enableAjaxErrorStatusText: true,
				disableFetchTracking: false,
				enableCorsCorrelation: true
			}
		});
		this.appInsights.loadAppInsights();
		this.appInsights.trackPageView();
		this.appInsights.addTelemetryInitializer((envelope) => {
			envelope.tags = envelope.tags || [];
			envelope.tags.push({ 'ai.cloud.role': 'gormclient' });
		});
	}

	logException(exception: Error) {
		// Don't spam AppInsights with angular exceptions
		let exceptionIdentifier = `${this.appInsights.context.getSessionId()}_${(exception.stack && exception.stack.length > 200 ? exception.stack.substring(0, 200) : exception.stack)}`;
		if (!this.exceptionHistory.some(ge => ge == exceptionIdentifier)) {
			this.exceptionHistory.push(exceptionIdentifier);
			this.appInsights.trackException({
				exception: exception,
				severityLevel: 3
			});
		}
	}

	getLogs(request: LogRequestModel): Promise<LogEvent[]> {
		return this.cleanSerializationValues(firstValueFrom(this.http.post<LogEvent[]>(`${this.baseUrl}`, request)));
	}

	getLogsForEntities<T extends Object>(entityType: Type<T>, request: LogRequestModel): Promise<LogEventOfEntity<T>[]> {
		return this.cleanSerializationValuesForEntities(firstValueFrom(this.http.post<LogEventOfEntity<T>[]>(`${this.baseUrl}`, request))).then(logs => {	
			return logs.map<LogEventOfEntity<T>>(entry => {
				return Object.assign(Object.assign(new LogEventOfEntity<T>(), entry), {
					user: this.userService.user(entry.userId),
					previousValueOfEntity: RelationService.instantiateModelsFromData(new entityType(), entry.previousValue),
					currentValueOfEntity: RelationService.instantiateModelsFromData(new entityType(), entry.currentValue)
				});
			});
		});
	}
	
	getLoggedEntityTypes() {
		return firstValueFrom(this.http.get<string[]>(`${this.baseUrl}/entity-types`));
	}

	private cleanSerializationValues(logPromise: Promise<LogEvent[]>) {
		return logPromise.then(logEntries => {
			logEntries.forEach(entry => {
				if (entry.currentValue) delete entry.currentValue['$id'];
				if (entry.previousValue) delete entry.previousValue['$id'];
			})
			return logEntries;
		});
	}

	private cleanSerializationValuesForEntities<T>(logPromise: Promise<LogEventOfEntity<T>[]>) {
		return logPromise.then(logEntries => {
			logEntries.forEach(entry => {
				if (entry.currentValue) delete entry.currentValue['$id'];
				if (entry.previousValue) delete entry.previousValue['$id'];
			})
			return logEntries;
		});
	}
}
